import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import GridCard from 'employee-platform/shared/components/GridCard'
import FlexContainer from 'core-system/FlexContainer'
import { GridContainer } from 'employee-platform/shared/styles/Grid'
import palette from 'core-system/Themes/palette'
import { Bike } from 'redux/bikeCatalog/bikeCatalogTypes'
import BikeCatalogCard from './BikeCatalogCard'
import BikeCatalogInfoBanner from './BikeCatalogInfoBanner'
import KingCountyHelpers from './KingCountyHelpers'
import ShortTermLeaseWaitlistSection from './ShortTermLeaseWaitlistSection'
import {
  TableManipulationItem,
  TableManipulationTopContainer,
} from 'core-system/Table'
import {
  BikeVendorFilter,
  getFilteredBikes,
  getVendorCounts,
  getVendorFilters,
} from '../utils'
import { CurrencyType } from 'features/Programs/Shared/types'
import { ConversionRates, formatCurrency } from 'shared/CurrencyFunctions'
import { formatVendorName } from '../shared/components/NonDirectCheckoutHelper'

const CatalogSection = styled.div`
  margin-top: 2rem;
`

const CatalogHeader = styled(Text)`
  margin-bottom: 1rem;
`

const EmptyState = styled(FlexContainer)`
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  gap: 0.5rem;
`

interface BikeCatalogProps {
  bikes: Bike[] | null
  subsidyBudgetCents: number
  currency: CurrencyType
  conversionRates: ConversionRates | null | undefined
  vendorCheckoutBikeId?: string | null
  vendorCheckoutError?: string | null
  onVendorCheckout: (bike: Bike) => void
}

const FilterTableContainer = styled(TableManipulationTopContainer)`
  margin-bottom: 1rem;
  border: 0;
  padding: 0;

  @media (max-width: ${({ theme }) => theme.screenSize.sm}) {
    border: 1px solid ${({ theme }) => theme.palette.grey.grey300};
    padding: 1rem;
  }
`

const FilterLabelSpan = styled(Text)`
  margin-left: 0.5rem;
`

const BikeCatalog = ({
  bikes,
  subsidyBudgetCents,
  currency,
  conversionRates,
  vendorCheckoutBikeId,
  vendorCheckoutError,
  onVendorCheckout,
}: BikeCatalogProps) => {
  const [selectedVendor, setSelectedVendor] = useState<BikeVendorFilter>('all')

  const vendorCounts = useMemo(() => getVendorCounts(bikes), [bikes])
  const vendorFilters = useMemo(() => getVendorFilters(bikes), [bikes])

  const filteredBikes = useMemo(
    () => getFilteredBikes(bikes, selectedVendor),
    [bikes, selectedVendor]
  )

  const subsidyAmountLabel = formatCurrency(
    currency,
    subsidyBudgetCents,
    conversionRates || {}
  )

  return (
    <CatalogSection>
      <CatalogHeader variant='h3'>Browse Eligible Rides</CatalogHeader>
      <BikeCatalogInfoBanner subsidyAmountLabel={subsidyAmountLabel} />
      <KingCountyHelpers />
      {vendorCheckoutError ? (
        <Text variant='body2' textColor={palette.text.secondary}>
          {vendorCheckoutError}
        </Text>
      ) : null}
      {bikes && bikes.length > 0 ? (
        <FilterTableContainer>
          {vendorFilters.map((vendor) => (
            <TableManipulationItem
              key={vendor}
              active={selectedVendor === vendor}
              disabled={vendor !== 'all' && (vendorCounts[vendor] || 0) === 0}
              onClick={() => setSelectedVendor(vendor)}
            >
              {vendorCounts[vendor] || 0}
              <FilterLabelSpan
                variant='action4'
                color={
                  selectedVendor === vendor
                    ? palette.white
                    : vendor !== 'all' && (vendorCounts[vendor] || 0) === 0
                      ? palette.text.disabled
                      : palette.secondary.purple700
                }
              >
                {vendor === 'all' ? 'All' : formatVendorName(vendor)}
              </FilterLabelSpan>
            </TableManipulationItem>
          ))}
        </FilterTableContainer>
      ) : null}

      {filteredBikes.length > 0 ? (
        <GridContainer numColumns={4} customGridGap='1rem'>
          {filteredBikes.map((bike) => (
            <BikeCatalogCard
              key={bike.id}
              bike={bike}
              subsidyBudgetCents={subsidyBudgetCents}
              isVendorCheckoutLoading={vendorCheckoutBikeId === bike.id}
              onVendorCheckout={onVendorCheckout}
            />
          ))}
        </GridContainer>
      ) : (
        <GridCard headerTitle='Bike Catalog'>
          <EmptyState>
            <Text variant='action3' textColor={palette.text.secondary}>
              {bikes && bikes.length > 0
                ? 'No bikes match this vendor.'
                : 'No bikes are currently available.'}
            </Text>
          </EmptyState>
        </GridCard>
      )}
      <ShortTermLeaseWaitlistSection />
    </CatalogSection>
  )
}

export default BikeCatalog
